import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { PPA_EXPENSE_CLASS_LABEL, PPA_EXPENSE_CLASS_ORDER } from '../../../../lib/expenseClass';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goalSectorsApi } from '../budgetGoalsApi';
import type { GoalSectorTreeNode } from '../budgetGoalTypes';
import { ppasApi } from '../ppasApi';
import type { PpaListItem } from '../ppaTypes';
import { samplePpa } from '../utils/samplePpa';

/**
 * A department's Program, Project or Activity — its own facts, and nothing beneath it.
 *
 * **The PPA is created EMPTY and its lines are added afterwards**, from the profile, one
 * expense class at a time. That is the reference's own flow and the reason its department
 * list carries a banner reading *add line items to submit*. This form carried them until
 * 21 Sep 2026, on the SALN filing's one-transaction reasoning — which fits a record filed in
 * one sitting and not one a department builds over days and a reviewer then adjusts.
 *
 * **The PLAN and the DEPARTMENT are stated, never asked for.** The reader chose both by
 * pressing a roster row, so the form has no picker for either — they read as facts in the
 * Table Display View, which is what that treatment is for.
 */
export function PpaModal({
  open,
  budgetPlanId,
  planNumber,
  orgUnitId,
  orgUnitName,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  budgetPlanId: string;
  planNumber: string;
  orgUnitId: string;
  orgUnitName: string;
  /**
   * The goals the PLAN adopted — not the whole catalog.
   *
   * This is the constraint the module exists for, expressed in the picker: the server refuses
   * a goal outside the plan's Goal Setting however live it is in the catalog, so offering one
   * would be offering a value the save rejects.
   */
  editing?: PpaListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [goalId, setGoalId] = useState<string | null>(editing?.goalId ?? null);
  const [expenseClass, setExpenseClass] = useState<number | null>(editing?.expenseClass ?? null);
  const [expectedOutcome, setExpectedOutcome] = useState(editing?.expectedOutcome ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    THE FUND SOURCE IS THE EXPENSE CLASS — product call, 06 Oct 2026: "fundsource = expense
    classes". It was a picker over the Fund Sources catalog; that catalog is gone, and the
    three classes are an enum, so the options are fixed and need no fetch. In the form's own
    order, never alphabetical (`sort="none"`).
  */
  const fundSourceOptions = useMemo<SelectOption[]>(
    () =>
      PPA_EXPENSE_CLASS_ORDER.map((value) => ({
        value: String(value),
        label: PPA_EXPENSE_CLASS_LABEL[value],
      })),
    [],
  );

  /*
    LOCKED while the PPA has lines — every line follows this class, so changing it would
    re-file all of them, and the server refuses it. Disabled for STATE, with the reason in
    the field's own description. `lineItemCount` counts the live UNEXCLUDED lines; an excluded
    one still holds the class, and the server's refusal names the field if that is the case.
  */
  const fundSourceLocked = (editing?.lineItemCount ?? 0) > 0;

  /*
    SECTOR → SUB-SECTOR → GOAL, as three pickers rather than one.

    It was a single flat list with the sector and sub-sector as each option's sublabel, which
    is not what the reference asks. Its form walks the framework down: choose the sector, then
    the sub-sector inside it, then the goal inside that — which is the picker rule this product
    already writes down for a container and its member, one rung deeper.

    The tree is what makes that possible in one request: three paged lists cannot narrow each
    other, and three round trips as the reader works down the form would show them an empty
    picker while each one lands.
  */
  const [tree, setTree] = useState<GoalSectorTreeNode[]>([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [sectorId, setSectorId] = useState<string | null>(null);
  const [subSectorId, setSubSectorId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    goalSectorsApi
      .tree(false, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;

        setTree(rows);

        /*
          SEEDED FROM THE RECORD on an edit — the reader opened this to correct something, and
          a cascade that arrived empty would make them re-answer two questions to get back to
          the goal they already had. The tree is the only thing that knows which sector and
          sub-sector a goal sits under, so it is seeded here rather than from the row.
        */
        if (!editing?.goalId) return;

        for (const sector of rows) {
          for (const sub of sector.subSectors) {
            if (sub.goals.some((g) => g.id === editing.goalId)) {
              setSectorId(sector.id);
              setSubSectorId(sub.id);
              return;
            }
          }
        }
      })
      .catch(() => {
        // Fails soft: the rest of the form still works, and the server refuses an unknown id
        // anyway — it is the authority on what may be aligned to.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingGoals(false);
      });

    return () => controller.abort();
  }, [editing?.goalId]);

  const sectorOptions = useMemo<SelectOption[]>(
    () => tree.map((s) => ({ value: s.id, label: s.name })),
    [tree],
  );

  const chosenSector = tree.find((s) => s.id === sectorId) ?? null;

  const subSectorOptions = useMemo<SelectOption[]>(
    () => (chosenSector?.subSectors ?? []).map((s) => ({ value: s.id, label: s.name })),
    [chosenSector],
  );

  const chosenSub = chosenSector?.subSectors.find((s) => s.id === subSectorId) ?? null;

  const goalOptions = useMemo<SelectOption[]>(
    () =>
      (chosenSub?.goals ?? []).map((g) => ({
        value: g.id,
        label: g.name,
        /*
          THE GOAL'S OWN OBJECTIVE AND STRATEGY, under its name in the option — product call,
          06 Oct 2026. They are what the reader is choosing between, so they are shown at the
          moment of choosing rather than in a table that appears only after a pick. They stay
          SEARCHABLE too: a reader looking for what the organization said about maternal health
          types those words, not the label.
        */
        details: [
          { label: 'Objective', value: g.objective },
          { label: 'Strategy', value: g.strategy },
        ],
        keywords: [g.objective, g.strategy].filter(Boolean).join(' '),
      })),
    [chosenSub],
  );

  /*
    CLEARED DOWNWARD when a rung above changes, or the form posts a goal that no longer sits
    under the sub-sector on screen — the member-clears-with-its-container rule.
  */
  function pickSector(next: string | null) {
    setSectorId(next);
    setSubSectorId(null);
    setGoalId(null);
  }

  function pickSubSector(next: string | null) {
    setSubSectorId(next);
    setGoalId(null);
  }

  const canSave = name.trim().length > 0 && expenseClass !== null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      budgetPlanId,
      orgUnitId,
      name: name.trim(),
      goalId,
      expenseClass,
      expectedOutcome: expectedOutcome.trim() || null,
    };

    try {
      if (editing) {
        await ppasApi.update(editing.id, request);
      } else {
        await ppasApi.create(request);
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this PPA.', 0);

      setError(failure);

      focusValidationError(failure, {
        idPrefix: 'ppa',
        /*
          The two refusals this form has no CONTROL for. The plan and the department are
          stated rather than asked for, so `ppa-budget-plan-id` and `ppa-org-unit-id` are
          ids no input was ever going to carry — and an unmapped field logs a development
          error and moves nothing, which reads as the feature not being wired.

          Both mean the same thing: something was deleted underneath the reader while this
          modal was open, and nothing on the form fixes it. Cancel is the way out, so that
          is where focus goes, with the banner above stating what happened.
        */
        fieldIds: { budgetPlanId: 'ppa-cancel', orgUnitId: 'ppa-cancel' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.ppaNumber}` : 'File a PPA'}
      size="2xl"
      id="ppa-modal"
      autogenerate={{
        /*
          Fills every required field so the primary action enables on the first try, and the
          optional ones the form can answer honestly — a record generated with only its
          required fields produces a roster row with nothing on it, which exercises nothing.

          It leaves the GOAL alone when the plan has adopted none: an id the plan did not
          select is refused by the server, which reads as a bug in the form.
        */
        onGenerate: () => {
          const sample = samplePpa();

          setName(sample.name);
          setExpectedOutcome(sample.expectedOutcome);

          if (goalOptions.length > 0) {
            setGoalId(goalOptions[Math.floor(Math.random() * goalOptions.length)].value);
          }

          // REQUIRED, so always filled — unless lines already hold it, when it is not the
          // generator's to move.
          if (!fundSourceLocked) {
            setExpenseClass(
              PPA_EXPENSE_CLASS_ORDER[Math.floor(Math.random() * PPA_EXPENSE_CLASS_ORDER.length)],
            );
          }
        },
        disabled: saving,
      }}
      confirm={{
        id: 'ppa-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppa-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppa-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          WHAT THE READER ALREADY DECIDED, stated rather than asked for. The Table Display
          View is for read-only facts on a form exactly as it is on a profile: the reader
          chose the plan and the department by pressing a roster row, and a picker locked to
          one value would be a control that collects nothing.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Budget plan', value: planNumber, mono: true },
            { label: 'Department', value: orgUnitName },
            ...(editing ? [{ label: 'PPA number', value: editing.ppaNumber, mono: true }] : []),
          ]}
        />

        <TextField
          id="ppa-name"
          label="Name"
          required
          description="What this Program, Project or Activity is called."
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error?.fieldError('name')}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {/*
            THE CASCADE. Each rung is DRAWN ALWAYS and DISABLED until the one above it is
            answered — never hidden, because a required field that appears out of nowhere
            pushes every field under it down the form as the reader works. Disabled for STATE,
            with the reason in its own description, which is where `<Select>` carries one.

            `sort="none"` at every rung: the catalog's own order is what the investment
            programme prints, and it is the order the reader will meet these in again.
          */}
          <Select
            id="ppa-sector-id"
            label="Sector"
            required
            description="The part of the programme this activity belongs to."
            value={sectorId}
            onChange={pickSector}
            options={sectorOptions}
            loading={loadingGoals}
            sort="none"
            disabled={saving}
          />

          <Select
            id="ppa-sub-sector-id"
            label="Sub-sector"
            required
            description={
              sectorId
                ? 'The sub-sector inside that sector.'
                : 'Choose a sector first — the sub-sectors belong to it.'
            }
            value={subSectorId}
            onChange={pickSubSector}
            options={subSectorOptions}
            sort="none"
            disabled={saving || !sectorId}
          />

          {/*
            THE GOAL TAKES THE WHOLE ROW — product call, 06 Oct 2026 — because each option now
            carries the goal's objective and strategy beneath its name, and those are sentences
            a half-width panel would clip to a few words. The span is for prose, and these
            options are prose.

            That also retired the Objective / Strategy table that used to appear under the
            pickers once a goal was chosen: the reader now reads both while choosing, and the
            PPA profile's Goal alignment card states them afterwards.
          */}
          <div className="sm:col-span-2">
            <Select
              id="ppa-goal-id"
              label="Goal"
              required
              description={
                subSectorId
                  ? 'What this activity is aligned to. Each goal shows its objective and strategy.'
                  : 'Choose a sub-sector first — the goals belong to it.'
              }
              value={goalId}
              onChange={setGoalId}
              options={goalOptions}
              sort="none"
              disabled={saving || !subSectorId}
              error={error?.fieldError('goalId')}
            />
          </div>

          <Select
            id="ppa-expense-class"
            label="Fund source"
            required
            description={
              fundSourceLocked
                ? 'This PPA already has budgeted lines, and every line follows its fund source — delete them first to change it.'
                : 'The expense class this activity is funded under. Every budgeted line on the PPA is filed under it.'
            }
            value={expenseClass === null ? null : String(expenseClass)}
            onChange={(next) => setExpenseClass(next === '' ? null : Number(next))}
            options={fundSourceOptions}
            sort="none"
            searchable={false}
            disabled={saving || fundSourceLocked}
            error={error?.fieldError('expenseClass')}
          />
        </div>

        {!loadingGoals && goalOptions.length === 0 ? (
          <p className="text-xs text-slate-500">
            There are no goals in the programme yet, so there is nothing to align to. The PPA
            can still be filed and pointed at one once the Goal Setting is set up.
          </p>
        ) : null}

        {/* A free note about the whole activity, so full width outside the grid. */}
        <Textarea
          id="ppa-expected-outcome"
          label="Expected outcome"
          description="What this activity is meant to achieve. Optional."
          value={expectedOutcome}
          onChange={(e) => setExpectedOutcome(e.target.value)}
          error={error?.fieldError('expectedOutcome')}
          disabled={saving}
        />

        {editing ? null : (
          <p className="text-xs text-slate-500">
            The PPA is filed empty. Its budgeted lines are added afterwards, one expense class
            at a time, from the PPA&rsquo;s own page.
          </p>
        )}
      </div>
    </Modal>
  );
}
